import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck2, Users, Percent, Loader2, ClipboardCheck, AlertOctagon, CalendarClock, ShieldCheck, CheckCircle2, ArrowRight, HeartHandshake, Target, Sparkles, UserPlus } from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, AreaChart, Area } from "recharts";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";

const TEAL = "#14b8a6";
const PP_COLORS = { a_jour: "#10b981", mois_prochain: "#84cc16", a_actualiser: "#f59e0b", en_retard: "#ef4444", brouillon: "#94a3b8" };

const PpKpi = ({ icon: Icon, label, value, suffix, accent = TEAL, i, tid, onClick }) => {
  const clickable = !!onClick;
  return (
    <motion.button type="button" disabled={!clickable} onClick={onClick}
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
      whileHover={clickable ? { y: -3 } : undefined}
      className={`text-left rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 backdrop-blur-sm relative overflow-hidden group ${clickable ? "cursor-pointer hover:ring-white/25" : "cursor-default"}`}
      data-testid={`pp-kpi-${tid}`}>
      <span className="absolute left-0 top-0 h-full w-1" style={{ background: accent }} />
      <div className="flex items-center gap-2" style={{ color: accent }}>
        <Icon className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 text-3xl font-extrabold text-white">{value}<span className="text-base font-semibold text-slate-400">{suffix}</span></div>
      {clickable && <ArrowRight className="h-4 w-4 text-slate-500 absolute right-4 bottom-4 opacity-0 group-hover:opacity-100 transition" />}
    </motion.button>
  );
};

const Panel = ({ title, children, className = "" }) => (
  <div className={`rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 ${className}`}>
    <h3 className="text-sm font-semibold text-slate-200 mb-4">{title}</h3>
    {children}
  </div>
);

// Vue « Projets Personnalisés » réutilisable (dashboard Vie sociale + page dédiée Tableau de bord PP)
export default function PpDashboardView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pp, setPp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const { data } = await api.get("/pp/dashboard"); setPp(data); } catch { setPp(null); }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-32 text-slate-400" data-testid="pp-dash-loading"><Loader2 className="h-6 w-6 animate-spin mr-2" /> {t("pp.dashLoading")}</div>;
  }
  const k = pp?.kpis;
  const hasData = k && (k.residents > 0 || (pp?.repartition || []).some((r) => r.value > 0));
  if (!pp || !hasData) {
    return (
      <div className="py-24 text-center" data-testid="pp-dash-empty">
        <ClipboardCheck className="h-10 w-10 text-slate-600 mx-auto mb-3" />
        <p className="text-slate-400 max-w-md mx-auto">{t("pp.dashEmpty")}</p>
        <button onClick={() => navigate("/suivi-pp")} data-testid="pp-dash-empty-cta"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold px-5 py-2.5 text-sm transition">
          {t("pp.viewDetail")}<ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }
  const rep = (pp.repartition || []).filter((r) => r.value > 0);
  const eng = pp.engagement || {};
  const ech = pp.echeances || [];
  const evo = pp.evolution || [];
  const byRef = pp.by_referent || [];
  const monthLabel = (m) => { const [y, mo] = m.split("-"); return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString(undefined, { month: "short" }); };
  const goto = (filter) => navigate(`/suivi-pp${filter ? `?filter=${filter}` : ""}`);

  return (
    <div data-testid="pp-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <PpKpi i={0} tid="en_retard" icon={AlertOctagon} accent={PP_COLORS.en_retard} label={t("pp.kpi.en_retard")} value={k.en_retard} onClick={() => goto("en_retard")} />
        <PpKpi i={1} tid="a_actualiser" icon={CalendarClock} accent={PP_COLORS.a_actualiser} label={t("pp.kpi.a_actualiser")} value={k.a_actualiser} onClick={() => goto("a_actualiser")} />
        <PpKpi i={2} tid="mois_prochain" icon={CalendarCheck2} accent={PP_COLORS.mois_prochain} label={t("pp.kpi.mois_prochain")} value={k.mois_prochain} onClick={() => goto("mois_prochain")} />
        <PpKpi i={3} tid="a_jour" icon={CheckCircle2} accent={PP_COLORS.a_jour} label={t("pp.kpi.a_jour")} value={k.a_jour} onClick={() => goto("a_jour")} />
        <PpKpi i={4} tid="pct" icon={Percent} accent={TEAL} label={t("pp.kpi.pct_a_jour")} value={k.pct_a_jour} suffix=" %" />
        <PpKpi i={5} tid="residents" icon={Users} accent="#0ea5e9" label={t("pp.kpi.residents")} value={k.residents} onClick={() => goto("")} />
        <PpKpi i={6} tid="attestations" icon={ShieldCheck} accent="#6366f1" label={t("pp.kpi.attestations")} value={k.attestations} />
        <PpKpi i={7} tid="reunions" icon={CalendarCheck2} accent="#8b5cf6" label={t("pp.kpi.reunions")} value={k.reunions} onClick={() => goto("reunions")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title={t("pp.repartitionTitle")}>
          {rep.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={rep} dataKey="value" nameKey="key" innerRadius={60} outerRadius={95} paddingAngle={3}>
                  {rep.map((r) => <Cell key={r.key} fill={PP_COLORS[r.key] || "#94a3b8"} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, t(`pp.repLabel.${n}`, { defaultValue: n })]}
                  contentStyle={{ background: "#0f1e29", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-500 text-sm py-16 text-center">{t("pp.dashEmpty")}</p>}
          <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2">
            {rep.map((r) => (
              <span key={r.key} className="text-xs text-slate-300 flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: PP_COLORS[r.key] || "#94a3b8" }} />
                {t(`pp.repLabel.${r.key}`, { defaultValue: r.key })} ({r.value})
              </span>
            ))}
          </div>
        </Panel>

        <Panel title={t("pp.ppSection")}>
          <div className="space-y-3">
            {[["en_retard", PP_COLORS.en_retard], ["a_actualiser", PP_COLORS.a_actualiser], ["mois_prochain", PP_COLORS.mois_prochain], ["a_jour", PP_COLORS.a_jour]].map(([key, color]) => {
              const val = k[key] || 0;
              const denom = Math.max(k.residents || 1, 1);
              const pctBar = Math.round((val / denom) * 100);
              return (
                <button key={key} onClick={() => goto(key)} data-testid={`pp-bar-${key}`} className="w-full text-left group">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-300">{t(`pp.repLabel.${key}`)}</span>
                    <span className="font-semibold text-slate-200">{val}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full transition-all group-hover:brightness-125" style={{ width: `${pctBar}%`, background: color }} />
                  </div>
                </button>
              );
            })}
          </div>
          <button onClick={() => goto("")} data-testid="pp-view-detail"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold px-5 py-3 text-sm transition">
            {t("pp.viewDetail")}<ArrowRight className="h-4 w-4" />
          </button>
        </Panel>
      </div>

      {/* Indicateurs d'accompagnement PP × activités (repérer → accompagner → valoriser) */}
      <div className="mt-6 mb-2 flex items-center gap-2">
        <HeartHandshake className="h-4 w-4 text-teal-300" />
        <h3 className="text-sm font-semibold text-slate-200">{t("pp.engagement.title")}</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4 max-w-2xl">{t("pp.engagement.subtitle")}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <PpKpi i={0} tid="beneficiaires" icon={HeartHandshake} accent="#10b981" label={t("pp.engagement.beneficiaires")} value={eng.beneficiaires || 0} suffix={` / ${eng.habitants_concernes || 0}`} />
        <PpKpi i={1} tid="beneficiaires_pct" icon={Percent} accent={TEAL} label={t("pp.engagement.beneficiairesPct")} value={eng.beneficiaires_pct || 0} suffix=" %" />
        <PpKpi i={2} tid="objectifs_soutenus" icon={Target} accent="#6366f1" label={t("pp.engagement.objectifsSoutenus")} value={eng.objectifs_soutenus || 0} />
        <PpKpi i={3} tid="reco_pct" icon={Sparkles} accent="#f59e0b" label={t("pp.engagement.recoPct")} value={eng.reco_pct || 0} suffix=" %" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <PpKpi i={4} tid="actions_soutenues" icon={CheckCircle2} accent="#22c55e" label={t("pp.engagement.actionsSoutenues")} value={eng.actions_soutenues || 0} />
        <PpKpi i={5} tid="reco_touches" icon={CheckCircle2} accent="#0ea5e9" label={t("pp.engagement.recoTouches")} value={eng.reco_touches || 0} suffix={` / ${eng.reco_total || 0}`} />
        <PpKpi i={6} tid="reco_non_touches" icon={UserPlus} accent="#f97316" label={t("pp.engagement.recoNonTouches")} value={eng.reco_non_touches || 0} />
        <PpKpi i={7} tid="habitants_concernes" icon={Users} accent="#8b5cf6" label={t("pp.engagement.habitantsConcernes")} value={eng.habitants_concernes || 0} />
      </div>

      <Panel title={t("pp.evolutionTitle")} className="mb-4">
        {evo.length >= 2 ? (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={evo.map((e) => ({ ...e, label: monthLabel(e.month) }))}>
              <defs>
                <linearGradient id="evoGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={TEAL} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={TEAL} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip cursor={{ stroke: "#ffffff20" }} contentStyle={{ background: "#0f1e29", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} formatter={(v) => [`${v} %`, t("pp.kpi.pct_a_jour")]} />
              <Area type="monotone" dataKey="pct" stroke={TEAL} strokeWidth={2.5} fill="url(#evoGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="py-10 text-center">
            <div className="text-3xl font-extrabold text-white">{evo[0]?.pct ?? k.pct_a_jour} %</div>
            <p className="text-xs text-slate-400 mt-2 max-w-md mx-auto">{t("pp.evolutionHint")}</p>
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Panel title={t("pp.echeancesTitle")}>
          {ech.some((e) => e.count > 0) ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={ech.map((e) => ({ ...e, label: monthLabel(e.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#0f1e29", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} formatter={(v) => [v, t("pp.echeancesTitle")]} />
                <Bar dataKey="count" fill={TEAL} radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-500 text-sm py-16 text-center">{t("pp.dashEmpty")}</p>}
        </Panel>

        <Panel title={t("pp.byReferentTitle")}>
          {byRef.length ? (
            <div className="space-y-3" data-testid="pp-by-referent">
              {byRef.map((b) => {
                const tot = Math.max(b.total, 1);
                return (
                  <div key={b.referent}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-200 font-medium truncate">{b.referent}</span>
                      <span className="text-slate-400">{b.total}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-white/10 overflow-hidden flex">
                      <div style={{ width: `${(b.a_jour / tot) * 100}%`, background: PP_COLORS.a_jour }} title={`${t("pp.repLabel.a_jour")}: ${b.a_jour}`} />
                      <div style={{ width: `${(b.a_prevoir / tot) * 100}%`, background: PP_COLORS.a_actualiser }} title={`${t("pp.byReferentToPlan")}: ${b.a_prevoir}`} />
                      <div style={{ width: `${(b.en_retard / tot) * 100}%`, background: PP_COLORS.en_retard }} title={`${t("pp.repLabel.en_retard")}: ${b.en_retard}`} />
                    </div>
                  </div>
                );
              })}
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 pt-3 border-t border-white/10">
                {[["a_jour", PP_COLORS.a_jour], ["byReferentToPlan", PP_COLORS.a_actualiser], ["en_retard", PP_COLORS.en_retard]].map(([key, c]) => (
                  <span key={key} className="text-[11px] text-slate-400 flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full" style={{ background: c }} />
                    {t(key.startsWith("byReferent") ? `pp.${key}` : `pp.repLabel.${key}`)}
                  </span>
                ))}
              </div>
            </div>
          ) : <p className="text-slate-500 text-sm py-16 text-center">{t("pp.dashEmpty")}</p>}
        </Panel>
      </div>
    </div>
  );
}
